/**
 * The comment chat popup (SPEC `Comment Generation` § The chat popup, #82-#85).
 *
 * A stacking `Modal` over whatever raised it — a feed card, the post preview, a `/watch` row — with
 * a header context line and, in the body, a scrolling message list under a composer card docked over
 * its bottom edge.
 *
 * Every decision it renders comes from `commentChatPlan`; this file only maps a plan to markup and
 * a click to an endpoint. That split is deliberate: the interesting states (an unanswered user
 * tail, which endpoint Retry means, whether a bubble may be regenerated) are exactly the ones a
 * DOM-less test suite can still cover.
 *
 * Three things worth knowing about the wiring:
 *
 *  - **Mutations replace the cache, they never invalidate it.** #83-#85 answer with the whole #82
 *    payload, so a send is one request and one `setQueryData`; a refetch behind an open chat would
 *    be pure risk. The one exception is a *failure*: the user's message is already committed
 *    server-side (and a regenerate may already have superseded its assistant), so the list is
 *    re-read from the database before the error row goes up.
 *  - **Nothing is generated until it is asked for.** The popup opens on an empty chat; the first
 *    preset press or typed line is what creates the conversation (#83). "New chat" only *hides* the
 *    live one — the server archive rides the next #83 transaction, so closing without sending
 *    leaves the old chat exactly where it was.
 *  - **Markdown is rendered as text.** No `rehype-raw`: an assistant reply is untrusted input, and
 *    React's escaping is the only thing standing between it and the DOM.
 */

import {
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AI_MESSAGE_MAX_CHARS, type CommentChatDTO } from '@lw/shared';
import {
  getCommentChat,
  regenerateCommentChat,
  sendCommentChatMessage,
  startCommentChat,
} from '../../api/ai';
import { QK } from '../../api/queryKeys';
import { describeError, isApiError } from '../../api/client';
import {
  ArrowUpIcon,
  CheckIcon,
  CopyIcon,
  cx,
  ErrorState,
  Modal,
  RefreshIcon,
  SparkleIcon,
  Spinner,
} from '../../components';
import { useCopyToClipboard } from '../../lib/hooks';
import { useAiQuery } from '../settings/useAi';
import {
  CHAT_EMPTY_HINT,
  commentChatPlan,
  type ChatBubblePlan,
  type ChatPendingKind,
} from './chatState';

/**
 * A `409` for one of these is *busy*, not *unconfigured*: the chat is fine, it just cannot take
 * another turn right now. Anything else a `409` says is a readiness problem and opens the setup
 * state — the backstop for a `QK.ai` cache that went stale under an open popup.
 */
const BUSY_CONFLICT_REASONS = new Set(['in_flight', 'archived']);

/** Auto-grow ceiling for the composer, in px; past it the textarea scrolls (mirrored in `ai.css`). */
const INPUT_MAX_HEIGHT = 512;

/** `details.reason` of a not-ready `409`; `null` when the conflict was a busy one (or no conflict). */
function setupConflictReason(error: unknown): string | null {
  if (!isApiError(error) || error.status !== 409) return null;
  const details = error.details;
  const raw =
    typeof details === 'object' && details !== null
      ? (details as { reason?: unknown }).reason
      : undefined;
  if (typeof raw === 'string') return BUSY_CONFLICT_REASONS.has(raw) ? null : raw;
  // A reason-less 409 still means "not ready"; the plan supplies the fallback sentence.
  return '';
}

/**
 * The text behind a rendered node — what a Copy button on a fenced code block puts on the
 * clipboard. Walked off React children rather than the hast node so it needs no `hast` types.
 */
function nodeText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map((child) => nodeText(child as ReactNode)).join('');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
}

/** A fenced block plus the one-click Copy the spec asks for. */
function CodeBlock({ children }: { children?: ReactNode }) {
  const [copied, copy] = useCopyToClipboard();
  return (
    <div className="comment-chat__code">
      <pre>{children}</pre>
      <button
        type="button"
        className="btn-ghost btn-ghost--sm comment-chat__code-copy"
        onClick={() => copy(nodeText(children))}
      >
        {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

const MARKDOWN_COMPONENTS: Components = {
  // The chat lives in a modal; a link that navigated the app away would take the chat with it.
  a({ node: _node, children, ...rest }) {
    return (
      <a {...rest} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    );
  },
  pre({ node: _node, children }) {
    return <CodeBlock>{children}</CodeBlock>;
  },
};

function AssistantBubble({
  bubble,
  onRegenerate,
}: {
  bubble: ChatBubblePlan;
  onRegenerate: () => void;
}) {
  const [copied, copy] = useCopyToClipboard();
  return (
    <div className="comment-chat__row comment-chat__row--assistant">
      <div className="comment-chat__msg">
        <Markdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {bubble.content}
        </Markdown>
      </div>
      <div className="comment-chat__msg-actions">
        {/* Raw Markdown, not the rendered text: this is the paste-into-LinkedIn affordance, and
            what the user read is what they should get. */}
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          onClick={() => copy(bubble.content)}
        >
          {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        {bubble.canRegenerate ? (
          <button type="button" className="btn-ghost btn-ghost--sm" onClick={onRegenerate}>
            <RefreshIcon size={12} />
            Regenerate
          </button>
        ) : null}
      </div>
    </div>
  );
}

export type CommentChatDialogProps = {
  postId: number;
  onClose: () => void;
  /** Opens Settings → Comment Generation — hosted by the provider, per §12.1. */
  onOpenSettings: () => void;
};

export function CommentChatDialog({ postId, onClose, onOpenSettings }: CommentChatDialogProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflictReason, setConflictReason] = useState<string | null>(null);
  /**
   * "New chat", client-side: the id of the conversation the popup is pretending it does not have.
   * An id rather than a flag, so that a start which failed *after* its transaction (a 502 on the
   * generation) shows the new conversation the refetch brings back instead of hiding it too.
   */
  const [hiddenConversationId, setHiddenConversationId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const chatQuery = useQuery({
    queryKey: QK.aiChat(postId),
    queryFn: ({ signal }) => getCommentChat(postId, signal),
    // The popup is the only writer and its mutations answer with the whole state; a refetch behind
    // an open chat could only disagree with what is on screen.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
  const readinessQuery = useAiQuery();

  const apply = (payload: CommentChatDTO) => {
    queryClient.setQueryData(QK.aiChat(postId), payload);
    setErrorMessage(null);
    setConflictReason(null);
    // Whatever was hidden is archived server-side by now (a start) or was never the chat on screen
    // (a send/regenerate answers with the live one), so the client-side hide has nothing left to do.
    setHiddenConversationId(null);
  };

  const onMutationError = (error: unknown) => {
    const reason = setupConflictReason(error);
    if (reason !== null) {
      // The cached readiness was stale — re-read it and show the setup state instead of a chat.
      void queryClient.invalidateQueries({ queryKey: QK.ai });
      setConflictReason(reason);
      return;
    }
    // The user's turn is already committed (#83/#84) and #85 may already have superseded its
    // assistant, so the truth is in the database, not in what we last rendered.
    void queryClient.invalidateQueries({ queryKey: QK.aiChat(postId) });
    setErrorMessage(describeError(error));
  };

  const start = useMutation({
    mutationFn: (content: string) => startCommentChat(postId, content),
    onSuccess: apply,
    onError: onMutationError,
  });
  const send = useMutation({
    mutationFn: (vars: { conversationId: number; content: string }) =>
      sendCommentChatMessage(vars.conversationId, vars.content),
    // The draft is cleared per call, not here: a preset click goes through the same two mutations,
    // and it must never wipe a message the user is still writing.
    onSuccess: apply,
    onError: onMutationError,
  });
  const regenerate = useMutation({
    mutationFn: (conversationId: number) => regenerateCommentChat(conversationId),
    onSuccess: apply,
    onError: onMutationError,
  });

  const pending: ChatPendingKind = start.isPending
    ? 'start'
    : send.isPending
      ? 'send'
      : regenerate.isPending
        ? 'regenerate'
        : 'none';

  const plan = commentChatPlan({
    chat: chatQuery.data,
    readiness: readinessQuery.data,
    pending,
    errorMessage,
    conflictReason,
    hiddenConversationId,
  });

  /**
   * The one routing rule, shared by the composer and every preset button: an empty chat opens one
   * (#83), a live chat continues it (#84). `onSuccess` is passed **per call** rather than set on the
   * mutation, so that clearing the draft belongs to the submit that typed it — a preset press must
   * never wipe a message the user is still writing.
   */
  const sendContent = (content: string, onSuccess?: () => void) => {
    const options = onSuccess === undefined ? undefined : { onSuccess };
    if (plan.composerTarget === 'start') {
      start.mutate(content, options);
      return;
    }
    if (plan.conversationId === null) return;
    send.mutate({ conversationId: plan.conversationId, content }, options);
  };

  /** Retry for `retry: 'start'` — replay the exact content the failed start was given. */
  const replayStart = () => {
    if (start.variables !== undefined) start.mutate(start.variables);
  };
  const runRegenerate = () => {
    if (plan.conversationId === null) replayStart();
    else regenerate.mutate(plan.conversationId);
  };
  const submit = () => {
    const content = draft.trim();
    if (content.length === 0 || plan.inputDisabled) return;
    sendContent(content, () => setDraft(''));
  };

  // Grow the composer with what is typed, up to `INPUT_MAX_HEIGHT` (it scrolls past that), and
  // publish its height so the list can keep exactly that much room free underneath — the card is
  // docked over the list, not in it. Layout, not effect: both have to land in the same paint as the
  // text, or a wrapped line flashes clipped and the reserved room lags a frame behind. `auto` first,
  // because `scrollHeight` of an over-tall box never reports a smaller one.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const composer = composerRef.current;
    const scroll = scrollRef.current;
    if (input === null || composer === null || scroll === null) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, INPUT_MAX_HEIGHT)}px`;
    // A growing card would otherwise rise over the reply being answered.
    const wasAtBottom = scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 8;
    scroll.style.setProperty('--comment-chat-dock', `${composer.offsetHeight}px`);
    if (wasAtBottom) scroll.scrollTop = scroll.scrollHeight;
  }, [draft, plan.showComposer]);

  // Keep the newest turn in view; a reply that lands below the fold reads as nothing happening.
  // Keyed on *whether* there is an error, not on the object — the plan builds a fresh one each
  // render, and scrolling on every render would fight the user's own scrolling. The end of the
  // scroll *is* the last message: the list reserves the docked card's height below it.
  useEffect(() => {
    const scroll = scrollRef.current;
    if (scroll !== null) scroll.scrollTop = scroll.scrollHeight;
  }, [plan.bubbles.length, plan.typing, plan.error !== null]);

  /*
   * A fresh #77 answer supersedes the stale-cache backstop: the `409` above only said "what the
   * cache told us was out of date", so the moment a new readiness lands — a refetch, or the user
   * finishing setup in the modal stacked over this one — that answer is the truth and the popup
   * follows it. Without this the setup state would outlive the problem it reported.
   */
  const readinessData = readinessQuery.data;
  const seenReadiness = useRef(readinessData);
  useEffect(() => {
    if (readinessData === seenReadiness.current) return;
    seenReadiness.current = readinessData;
    setConflictReason(null);
  }, [readinessData]);

  const loadError = chatQuery.isError || readinessQuery.isError;

  return (
    <Modal
      open
      size="md"
      title="Generate Comment"
      onClose={onClose}
      className="comment-chat"
      headActions={
        plan.showNewChat ? (
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            disabled={!plan.canStartNewChat}
            // Client-side only: the live conversation is hidden, not archived. Whatever is sent
            // next carries the archive with it inside #83's transaction.
            onClick={() => {
              setHiddenConversationId(plan.conversationId);
              setErrorMessage(null);
            }}
          >
            <SparkleIcon size={13} />
            New chat
          </button>
        ) : null
      }
    >
      <div
        className={cx('comment-chat__scroll', plan.showComposer && 'comment-chat__scroll--docked')}
        ref={scrollRef}
      >
        {plan.context !== null ? (
          <div className="comment-chat__post">
            <strong className="comment-chat__author">{plan.context.authorName}</strong>
            {/* Server-truncated to one line (§12.5). */}
            <span className="muted comment-chat__preview">{plan.context.preview}</span>
          </div>
        ) : null}

        {loadError ? (
          <ErrorState
            title="Couldn't open this chat"
            error={chatQuery.error ?? readinessQuery.error}
            onRetry={() => {
              void chatQuery.refetch();
              void readinessQuery.refetch();
            }}
            retrying={chatQuery.isFetching || readinessQuery.isFetching}
          />
        ) : plan.view === 'loading' ? (
          <Spinner size="lg" label="Loading…" />
        ) : plan.view === 'setup' ? (
          <div className="comment-chat__setup">
            {/* The server's own sentence — the client never decides which step is missing. */}
            <p>{plan.setupReason}</p>
            <p className="muted">
              Settings → Comment Generation holds the AI backend, its API key, the prompts and the
              presets this composer offers. It is a one-time setup.
            </p>
            <button type="button" className="btn-primary" onClick={onOpenSettings}>
              Open Settings
            </button>
          </div>
        ) : (
          <div className="comment-chat__list">
            {/* The empty chat says what to do next; the server has no sentence for a chat that
                does not exist yet, so this is one of the file's authored strings. */}
            {plan.emptyChat && !plan.typing && plan.error === null ? (
              <p className="muted comment-chat__hint">{CHAT_EMPTY_HINT}</p>
            ) : null}

            {plan.bubbles.map((bubble) =>
              bubble.role === 'user' ? (
                <div className="comment-chat__row comment-chat__row--user" key={bubble.id}>
                  <div className="comment-chat__bubble">{bubble.content}</div>
                </div>
              ) : (
                <AssistantBubble key={bubble.id} bubble={bubble} onRegenerate={runRegenerate} />
              ),
            )}

            {plan.typing ? (
              <div className="comment-chat__row comment-chat__row--assistant">
                <div className="comment-chat__typing" role="status" aria-label="Writing a reply">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : null}

            {plan.error !== null ? (
              <div className="comment-chat__error" role="alert">
                {/* The server's message verbatim; no toast, no banner (SPEC § Errors). */}
                <span>{plan.error.message}</span>
                <button
                  type="button"
                  className="btn-ghost btn-ghost--sm"
                  onClick={plan.error.retry === 'start' ? replayStart : runRegenerate}
                >
                  <RefreshIcon size={12} />
                  Retry
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {plan.showComposer ? (
        <div className="comment-chat__composer" ref={composerRef}>
          <textarea
            ref={inputRef}
            className="comment-chat__input"
            rows={1}
            value={draft}
            maxLength={AI_MESSAGE_MAX_CHARS}
            disabled={plan.inputDisabled}
            placeholder="Ask for a shorter version, a different angle…"
            aria-label="Message"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
              // Enter sends, Shift+Enter breaks the line — the chat convention, and a comment
              // draft is a paragraph, so the newline has to stay reachable.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          <div className="comment-chat__composer-row">
            {/* One button per stored preset, its name verbatim (§12.5). Pressing it sends the
                preset's prompt as an ordinary message — starting the chat or continuing it — and
                leaves whatever is in the draft alone. The row stays even when empty so the send
                button keeps its right-hand edge. */}
            <div className="comment-chat__presets">
              {plan.showPresets
                ? plan.presets.map((preset, index) => (
                    <button
                      key={`${String(index)}-${preset.name}`}
                      type="button"
                      className="btn-ghost btn-ghost--sm"
                      disabled={plan.presetsDisabled}
                      onClick={() => sendContent(preset.prompt)}
                    >
                      {preset.name}
                    </button>
                  ))
                : null}
            </div>
            <button
              type="button"
              className="comment-chat__send"
              aria-label="Send"
              disabled={plan.inputDisabled || draft.trim().length === 0}
              onClick={submit}
            >
              {pending === 'send' || pending === 'start' ? (
                <Spinner size="sm" />
              ) : (
                <ArrowUpIcon size={14} />
              )}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

export default CommentChatDialog;
